import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, FileText, MoreVertical, Pencil, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const [list, setList] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = () => api.get("/templates").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const toggleActive = async (t) => {
    try {
      await api.patch(`/templates/${t.id}`, { active: t.active === false });
      toast.success(t.active === false ? "Modelo ativado" : "Modelo inativado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/templates/${pendingDelete.id}`);
      toast.success("Modelo excluído");
      setPendingDelete(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="templates-page">
      <PageHeader title="Modelos de Checklist" subtitle="Formulários dinâmicos para inspeções e manutenção"
        actions={<Link to="/templates/new"><Button className="gap-2" data-testid="new-template-btn"><Plus className="w-4 h-4" />Novo Modelo</Button></Link>} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => {
          const totalQ = (t.structure_json || []).reduce((s, sec) => s + (sec.questions?.length || 0), 0);
          const inactive = t.active === false;
          return (
            <Card key={t.id} className={`p-5 h-full flex flex-col relative ${inactive ? "opacity-60" : "hover:border-primary"} transition-colors`} data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <FileText className="w-6 h-6 text-primary" />
                <div className="flex items-center gap-1">
                  {inactive && <Badge variant="outline" className="text-[10px] text-muted-foreground">Inativo</Badge>}
                  {t.fuel_action === "transfer_to_comboio" && <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30 text-primary">Transferência</Badge>}
                  {t.fuel_action === "field_refuel" && <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30 text-primary">Abastecimento</Badge>}
                  <Badge variant="outline" className="text-[10px]">{t.trigger_type}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="w-7 h-7" data-testid={`menu-${t.id}`} onClick={(e) => e.preventDefault()}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem asChild data-testid={`action-edit-${t.id}`}>
                        <Link to={`/templates/${t.id}`} className="cursor-pointer">
                          <Pencil className="w-4 h-4 mr-2" /> Editar
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleActive(t)} data-testid={`action-toggle-${t.id}`}>
                        <Power className="w-4 h-4 mr-2" /> {inactive ? "Ativar" : "Inativar"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setPendingDelete(t)} className="text-destructive focus:text-destructive" data-testid={`action-delete-${t.id}`}>
                        <Trash2 className="w-4 h-4 mr-2" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <Link to={`/templates/${t.id}`} className="flex-1">
                <h3 className="font-display font-bold text-lg tracking-tight leading-tight hover:text-primary">{t.title}</h3>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description}</p>
              </Link>
              <div className="mt-4 flex justify-between text-xs text-muted-foreground">
                <span>{t.structure_json?.length || 0} seções</span>
                <span>{totalQ} perguntas</span>
              </div>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir modelo?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o modelo <b>{pendingDelete?.title}</b>? Esta ação não pode ser desfeita.
              Inspeções já enviadas com base neste modelo permanecerão registradas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
